import { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download } from "lucide-react";
import { Card } from "@/components/ui/card";

export default function Reports() {
  const [centers, setCenters] = useState([]);
  const [countries, setCountries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [f, setF] = useState({ start: "", end: "", center_id: "", country_id: "", project_id: "", status: "" });

  useEffect(() => {
    Promise.all([api.get("/centers"), api.get("/countries"), api.get("/projects")])
      .then(([c, co, pr]) => { setCenters(c.data); setCountries(co.data); setProjects(pr.data); });
  }, []);

  const downloadUrl = () => {
    const token = localStorage.getItem("attendly_token") || "";
    const params = new URLSearchParams({ token, ...Object.fromEntries(Object.entries(f).filter(([_,v]) => v)) });
    return `${API_BASE}/reports/attendance.csv?${params.toString()}`;
  };

  return (
    <>
      <PageHeader eyebrow="Reports" title="Attendance Reports" subtitle="Filter and export CSV" />
      <div className="p-8 space-y-6">
        <Card className="rounded-none border-border p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div><Label className="label-mono">From</Label><Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="rounded-none" /></div>
            <div><Label className="label-mono">To</Label><Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="rounded-none" /></div>
            <div><Label className="label-mono">Country</Label>
              <Select value={f.country_id || "__all"} onValueChange={(v) => setF({ ...f, country_id: v === "__all" ? "" : v })}>
                <SelectTrigger className="rounded-none"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent className="rounded-none bg-card">
                  <SelectItem value="__all">All</SelectItem>
                  {countries.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="label-mono">Center</Label>
              <Select value={f.center_id || "__all"} onValueChange={(v) => setF({ ...f, center_id: v === "__all" ? "" : v })}>
                <SelectTrigger className="rounded-none"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent className="rounded-none bg-card">
                  <SelectItem value="__all">All</SelectItem>
                  {centers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="label-mono">Project</Label>
              <Select value={f.project_id || "__all"} onValueChange={(v) => setF({ ...f, project_id: v === "__all" ? "" : v })}>
                <SelectTrigger className="rounded-none"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent className="rounded-none bg-card">
                  <SelectItem value="__all">All</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="label-mono">Status</Label>
              <Select value={f.status || "__all"} onValueChange={(v) => setF({ ...f, status: v === "__all" ? "" : v })}>
                <SelectTrigger className="rounded-none"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent className="rounded-none bg-card">
                  <SelectItem value="__all">All</SelectItem>
                  <SelectItem value="Present">Present</SelectItem>
                  <SelectItem value="Late">Late</SelectItem>
                  <SelectItem value="Absent">Absent</SelectItem>
                  <SelectItem value="Missing_Punch_Out">Missing punch-out</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <a href={downloadUrl()} target="_blank" rel="noreferrer" data-testid="csv-download-link">
              <Button className="rounded-none btn-primary-brand">
                <Download className="h-4 w-4 mr-2" strokeWidth={1.5} /> Export CSV
              </Button>
            </a>
          </div>
        </Card>

        <Card className="rounded-none border-border p-6">
          <div className="label-mono mb-2">Included report types</div>
          <ul className="text-sm text-muted-foreground grid grid-cols-2 lg:grid-cols-3 gap-2">
            <li>· Daily / Weekly / Monthly</li>
            <li>· Employee summary</li>
            <li>· Country-wise</li>
            <li>· Center-wise</li>
            <li>· Project-wise</li>
            <li>· Late arrivals</li>
            <li>· Absentees</li>
            <li>· Missing punch-outs</li>
            <li>· GPS verification</li>
          </ul>
        </Card>
      </div>
    </>
  );
}
